import React from "react";
import { Check, CardList, Clock } from "react-bootstrap-icons";
import { cn, Chip, Card, Switch } from "@heroui/react";

import { findFirstQuestion } from "@/lib/journey-utils";

import { JourneyQuestion } from "../../types/dataSchema";
import { useEstimatedTime } from "../hooks/useEstimatedTime";
import { useAdminStatus } from "../hooks/useAdminStatus";
import { useJourneyData } from "../JourneyDataClientProvider";

import { useAnswerContext } from "./AnswerContext";
import { getNextQuestionId } from "./nextQuestionAlgorithm";
import { computeRecommendationKeys } from "./computeRecommendationKeys";

interface LiveSummaryProps {
  questions: JourneyQuestion[];
}

export default function LiveSummary({ questions }: LiveSummaryProps) {
  const {
    answers,
    currentQuestionId: contextCurrentQuestionId,
    showRecommendations,
    handleGoBackToQuestion,
  } = useAnswerContext();
  const { remainingSeconds, displayText } = useEstimatedTime();
  const {
    isAdmin,
    showRecommendations: showAdminRecommendations,
    toggleRecommendations,
  } = useAdminStatus();
  const { userData } = useJourneyData();

  // Calculate the current valid journey path based on current answers
  const getCurrentValidPath = (): string[] => {
    if (!questions.length) return [];

    // Build journey data from userData to use with findFirstQuestion
    const journeyData = {
      questions: userData.questions,
      recommendations: userData.recommendations,
    };
    const firstQuestion = findFirstQuestion(journeyData);

    if (!firstQuestion) return [];

    const path: string[] = [];
    let currentQuestion: string | null = firstQuestion.id;
    const visited = new Set<string>();

    while (currentQuestion && !visited.has(currentQuestion)) {
      visited.add(currentQuestion);
      path.push(currentQuestion);

      // If this is the current question, stop here
      if (currentQuestion === contextCurrentQuestionId) {
        break;
      }

      // Check if this question has an answer
      const answer = answers[currentQuestion];

      if (!answer) {
        // No answer yet, this is where the path ends
        break;
      }

      // Find the next question based on current answers
      const nextId = getNextQuestionId({
        questions,
        questionHistory: path,
        currentQuestionId: currentQuestion,
        skipped: answer === "(skipped)",
        selectedOptions: Array.isArray(answer) ? answer : [answer],
        answers,
      });

      currentQuestion = nextId;
    }

    return path;
  };

  const validPath = getCurrentValidPath();

  // Calculate recommendations for admin users only when toggle is enabled
  const recommendedRecommendationsKeys =
    isAdmin && showAdminRecommendations
      ? computeRecommendationKeys(
          questions,
          answers,
          userData.recommendations,
          validPath,
        )
      : null;

  // Helper function to get recommendations for a specific answer (admin only)
  const getRecommendationsForAnswer = (
    questionId: string,
    answerValue: string,
  ) => {
    if (
      !isAdmin ||
      !showAdminRecommendations ||
      !recommendedRecommendationsKeys
    )
      return null;

    const question = questions.find((q) => q.id === questionId);

    if (!question) return null;

    const option = question.options.find(
      (opt) => opt.value === answerValue.split(":")[0],
    );

    if (!option) return null;

    const strongRecs = option.strong_recommendations || [];
    const optionalRecs = option.optional_recommendations || [];
    const excluded = option.exclude_recommendations || [];

    if (
      strongRecs.length === 0 &&
      optionalRecs.length === 0 &&
      excluded.length === 0
    ) {
      return null;
    }

    return {
      strong: strongRecs,
      optional: optionalRecs,
      excluded: excluded,
    };
  };

  // Format remaining time for display in chip
  const formatTimeForChip = (seconds: number): string => {
    if (seconds === 0) return "Done!";
    if (seconds <= 10) return "Almost done!";
    if (seconds <= 60) return `${seconds}s left`;
    const minutes = Math.ceil(seconds / 60);

    return `${minutes}min left`;
  };

  // Only show answered questions that are in the current valid path, in the correct order
  const answeredEntries = validPath
    .filter((questionId) => {
      const value = answers[questionId];

      // Check if question has a valid answer
      if (Array.isArray(value)) {
        return value.length > 0;
      }

      return value !== undefined && value !== null && value !== "";
    })
    .map((questionId) => [questionId, answers[questionId]] as [string, any]);

  // Add current question to the list even if not answered yet (for chip display)
  const displayEntries = [...answeredEntries];

  if (
    contextCurrentQuestionId &&
    !answeredEntries.find(([id]) => id === contextCurrentQuestionId)
  ) {
    displayEntries.push([contextCurrentQuestionId, ""]);
  }

  if (displayEntries.length === 0) {
    return null;
  }

  return (
    <div className={cn("w-full", "md:max-w-lg", "mx-auto", "p-4")}>
      <Card className="p-8 bg-gradient-to-br from-slate-800/80 to-slate-900/60 border border-slate-600/30 backdrop-blur-sm hover:border-slate-500/50 transition-all duration-300 max-h-[90vh] flex flex-col">
        {/* Header Section */}
        <div className="text-center mb-6 shrink-0">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-secondary-400 to-indigo-700 rounded-full mb-4">
            <CardList className="w-6 h-6 text-white" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">Your Journey</h3>
          <p className="text-gray-400 text-sm">
            Track your progress through the onboarding experience
          </p>
        </div>

        {/* Progress Content - Scrollable */}
        <div className="flex-1 overflow-y-auto overflow-x-clip min-h-0">
          <div className="space-y-4">
            {displayEntries.length > 0 ? (
              <div className="space-y-3">
                {displayEntries.map(([questionId, value]) => {
                  const question = questions.find((q) => q.id === questionId);

                  if (!question) {
                    return null;
                  }

                  const values = Array.isArray(value) ? value : [value];
                  let displayValue = "";
                  const isAnswered =
                    value !== "" &&
                    value !== undefined &&
                    value !== null &&
                    (Array.isArray(value) ? value.length > 0 : true);

                  if (values.length > 0 && isAnswered) {
                    displayValue = values
                      .map((val) => {
                        const originalValue = val.split(":")[0];
                        const editableText =
                          val.indexOf(":") > -1
                            ? val.split(":").slice(1).join(":")
                            : null;

                        const option = question.options.find(
                          (o) => o.value === originalValue,
                        );

                        return editableText || option?.label || val;
                      })
                      .join(", ");
                  }

                  const isCurrent = questionId === contextCurrentQuestionId;
                  const isClickable = isAnswered && !isCurrent;

                  const handleQuestionClick = () => {
                    if (isClickable && handleGoBackToQuestion) {
                      handleGoBackToQuestion(questionId);
                    }
                  };

                  return (
                    <div
                      key={questionId}
                      className={cn(
                        "p-2 rounded-lg border transition-all duration-200 group",
                        isCurrent
                          ? "bg-blue-900/30 border-blue-500/30 shadow-lg shadow-blue-500/10"
                          : isAnswered
                            ? "bg-green-900/20 border-green-500/20"
                            : "bg-slate-700/20 border-slate-600/20",
                        isClickable &&
                          "hover:bg-green-900/40 hover:border-green-500/40 hover:shadow-lg hover:shadow-green-500/10 cursor-pointer transform hover:scale-[1.02]",
                      )}
                      role={isClickable ? "button" : undefined}
                      tabIndex={isClickable ? 0 : undefined}
                      title={
                        isClickable
                          ? `Go back to "${question.name}"`
                          : undefined
                      }
                      onClick={handleQuestionClick}
                      onKeyDown={
                        isClickable
                          ? (e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleQuestionClick();
                              }
                            }
                          : undefined
                      }
                    >
                      <div className="flex items-start gap-3">
                        {/* Status Icon */}
                        <div
                          className={cn(
                            "flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-0.5",
                            isAnswered
                              ? "bg-green-500"
                              : isCurrent
                                ? "bg-blue-500 animate-pulse"
                                : "bg-slate-500",
                          )}
                        >
                          {isAnswered ? (
                            <Check className="w-4 h-4 text-white" />
                          ) : isCurrent ? (
                            <Clock className="w-4 h-4 text-white" />
                          ) : (
                            <span className="w-2 h-2 bg-white rounded-full" />
                          )}
                        </div>

                        {/* Question Content */}
                        <div className="flex-grow min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4
                              className={cn(
                                "font-semibold text-sm flex items-center gap-1",
                                isCurrent
                                  ? "text-blue-300"
                                  : isAnswered
                                    ? "text-green-300"
                                    : "text-gray-400",
                                isClickable && "group-hover:text-green-200",
                              )}
                            >
                              {question.name}
                              {isClickable && (
                                <span className="text-xs opacity-50 ml-1 transition-opacity group-hover:opacity-80">
                                  ↑
                                </span>
                              )}
                            </h4>
                            {isCurrent &&
                              !showRecommendations &&
                              remainingSeconds > 0 && (
                                <Chip
                                  className="bg-blue-500/20 text-blue-300 border border-blue-400/20 text-xs"
                                  size="sm"
                                  variant="bordered"
                                >
                                  {formatTimeForChip(remainingSeconds)}
                                </Chip>
                              )}
                          </div>

                          {/* Answer Display */}
                          {isAnswered ? (
                            <div className="space-y-2">
                              <span
                                className={cn(
                                  "text-sm text-gray-300 break-words block",
                                  displayValue.length > 60 && "line-clamp-2",
                                )}
                              >
                                {displayValue}
                              </span>

                              {/* Admin Recommendation Chips - Better spacing */}
                              {isAdmin && showAdminRecommendations && (
                                <div className="flex flex-wrap gap-1 items-center">
                                  {values
                                    .map((val) => val.split(":")[0])
                                    .map((originalValue) => {
                                      const recs = getRecommendationsForAnswer(
                                        questionId,
                                        originalValue,
                                      );

                                      if (!recs) return null;

                                      const allRecs = [
                                        ...recs.strong.map((id) => ({
                                          id,
                                          type: "strong" as const,
                                        })),
                                        ...recs.optional.map((id) => ({
                                          id,
                                          type: "optional" as const,
                                        })),
                                        ...recs.excluded.map((id) => ({
                                          id,
                                          type: "excluded" as const,
                                        })),
                                      ];

                                      if (allRecs.length === 0) return null;

                                      return allRecs.map(({ id, type }) => {
                                        const recommendation =
                                          userData.recommendations.find(
                                            (s) => s.id === id,
                                          );
                                        const recommendationName =
                                          recommendation?.name || id;

                                        return (
                                          <Chip
                                            key={`${originalValue}-${id}`}
                                            className={cn(
                                              "border-0",
                                              type === "strong" &&
                                                "bg-green-500/20 text-green-300 hover:bg-green-500/30",
                                              type === "optional" &&
                                                "bg-blue-500/20 text-blue-300 hover:bg-blue-500/30",
                                              type === "excluded" &&
                                                "bg-red-500/20 text-red-300 hover:bg-red-500/30",
                                            )}
                                            size="sm"
                                            title={
                                              type === "strong"
                                                ? `Strongly recommended: ${recommendationName}`
                                                : type === "optional"
                                                  ? `Optionally recommended: ${recommendationName}`
                                                  : `Excluded by this answer: ${recommendationName}`
                                            }
                                            variant="flat"
                                          >
                                            <span className="text-xs font-medium">
                                              {recommendationName}
                                            </span>
                                          </Chip>
                                        );
                                      });
                                    })
                                    .flat()
                                    .filter(Boolean)}
                                </div>
                              )}
                            </div>
                          ) : isCurrent ? (
                            <p className="text-sm text-blue-200/70 italic">
                              Answering now...
                            </p>
                          ) : (
                            <p className="text-sm text-gray-500 italic">
                              Not answered yet
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-slate-700/50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CardList className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-400 text-sm">
                  Your answers will appear here as you progress through the
                  onboarding.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer Section - Fixed at bottom */}
        <div className="shrink-0">
          {/* Time Estimation Footer */}
          {displayText && !showRecommendations && (
            <div className="border-t border-slate-600/30 pt-4 mt-6">
              <div className="flex items-center justify-center gap-2 text-center">
                <Clock className="w-4 h-4 text-blue-400" />
                <p className="text-sm text-blue-300/80 italic">{displayText}</p>
              </div>
            </div>
          )}

          {/* Admin Toggle Footer */}
          {isAdmin && (
            <div className="border-t border-slate-600/30 pt-3 mt-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-400 rounded-full" />
                  <p className="text-xs text-blue-300/80 font-medium">
                    Admin: Live recommendations
                  </p>
                </div>
                <Switch
                  className="scale-75"
                  color="primary"
                  isSelected={showAdminRecommendations}
                  size="sm"
                  onChange={toggleRecommendations}
                />
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
